import React, { FunctionComponent, Reducer, useReducer, useState } from "react";
import { Trans } from "@lingui/macro";
import { TaxCodeChangeEvent, TaxCodeField } from "./TaxCodeField";
import { EnteredTaxCodesTable } from "./EnteredTaxCodesTable";
import { HelpKapitaalaflossingBouwsparen } from "./helpDialogs/HelpKapitaalaflossingBouwsparen";
import { HelpSchuldSaldoVerzekeringBouwsparen } from "./helpDialogs/HelpSchuldSaldoVerzekeringBouwsparen";
import { HelpSchuldSaldoVerzekeringBouwsparenBefore89 } from "./helpDialogs/HelpSchuldSaldoVerzekeringBouwsparenBefore89";
import { HelpKapitaalaflossingLangetermijnsparen } from "./helpDialogs/HelpKapitaalaflossingLangetermijnsparen";
import { useAangifte } from "components/aangifte/AangifteProvider";
import { useQuickEntryRubriek } from "./useQuickEntryRubriek";
import {ValueBuitenland} from "../../../domain/types";
import _ from "lodash";

export interface Props {}

interface SetValueAction {
  type: "SET_VALUE";
  value?: boolean | string | number | ValueBuitenland;
  error?: JSX.Element;
  partialEntry?: boolean;
}
interface SetCodeAction {
  type: "SET_CODE";
  value: string;
}
interface ResetAction {
  type: "RESET";
}
type Action = SetValueAction | SetCodeAction | ResetAction;
interface State {
  code: string;
  value?: boolean | string | number | ValueBuitenland;
  error?: JSX.Element;
  partialEntry?: boolean;
}
const taxCodesReducer: Reducer<State, Action> = (state, action) => {
  switch (action.type) {
    case "RESET":
      return { code: "" };
    case "SET_CODE":
      return { code: action.value };
    case "SET_VALUE":
      return { ...state, value: action.value, error: action.error, partialEntry: action.partialEntry };
  }
};

const valueToFieldValue = (value: any) => {
  const isEmptyObject = _.isObjectLike(value) && (Object.values(value).length === 0 || Object.values(value).every(v => v === "" || v === 0 || v === null || v === undefined));
  if (value === 0 || value === "" || isEmptyObject) return undefined;
  return value;
};

export const TaxCodesForm: FunctionComponent<Props> = () => {
  const codeInputRef = React.useRef<HTMLInputElement | null>(null);
  const { dispatch: dispatchAangifte } = useAangifte();
  const [{ code, value, error: valueError, partialEntry }, dispatch] = useReducer(taxCodesReducer, { code: "" });
  const { rubriek, error: rubriekError } = useQuickEntryRubriek(code);
  const [taxCodesOrderedByEntry, setTaxCodesOrderedByEntry] = useState(new Set<string>());
  const disableAddingCode = !rubriek || !!valueError || !!partialEntry;

  const handleCodeChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: "SET_CODE", value: event.target.value });
  }, []);

  const handleValueChange = React.useCallback(({ error, value, partialEntry }: TaxCodeChangeEvent) => {
    dispatch({ type: "SET_VALUE", value, error, partialEntry });
  }, []);

  const handleAddCode = () => {
    if (disableAddingCode) return;

    dispatchAangifte({ code, value: valueToFieldValue(value), type: "UPDATE_RUBRIEK_VALUE" });
    setTaxCodesOrderedByEntry(new Set([code, ...taxCodesOrderedByEntry]));
    dispatch({ type: "RESET" });
    codeInputRef.current?.focus();
  };

  const handleEnterPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      handleAddCode();
    }
  };

  const handleClearCode = () => {
    dispatch({ type: "RESET" });
    codeInputRef.current?.focus();
  };

  return (
    <div id="codes" className="breed" style={{"padding": "0px"}}>
      <div onKeyDown={handleEnterPress}>
        <div className="codesTitles">
          <div className="veld">
            <Trans>Code</Trans>
          </div>
          <div className="veld" id="amountlabel">
            {(() => {
              switch (rubriek?.type) {
                case "bool":
                  return <Trans>Ja / Neen</Trans>;
                case "num":
                  return <Trans>Aantal</Trans>;
                case "date":
                  return <Trans>Datum</Trans>;
                default:
                  return <Trans>Bedrag</Trans>;
              }
            })()}
          </div>
        </div>
        <div className="veld" style={{ clear: "left" }}>
          <input
            maxLength={4}
            type="text"
            id="code"
            name="code"
            onChange={handleCodeChange}
            value={code}
            ref={codeInputRef}
            autoFocus
          />
        </div>
        <div className="veld" id="amount" style={{ width: "auto", minWidth: "100px" }} >
          {rubriek ? <TaxCodeField key={code} rubriek={rubriek} onChange={handleValueChange} /> : null}
        </div>
        <div className="veld" style={{ width: "auto", marginLeft: "15px" }}>
          <button type="button" disabled={disableAddingCode} onClick={handleAddCode}>
            <Trans>Voeg toe</Trans>
          </button>
        </div>
        <div className="veldbreed" id="descr" style={{ width: "590px" }}>
          {(() => {
            if (rubriekError && code.length === 4) {
              return <span className="error">{rubriekError}</span>;
            }
            if (valueError && !partialEntry) {
              return <span className="error">{valueError}</span>;
            }
            return rubriek?.name;
          })()}&nbsp;
        </div>
      </div>

      <EnteredTaxCodesTable taxCodesOrderedByEntry={taxCodesOrderedByEntry} />

      {rubriek && (
        <>
          <HelpKapitaalaflossingBouwsparen code={code} onClose={handleClearCode} />
          <HelpSchuldSaldoVerzekeringBouwsparen code={code} onClose={handleClearCode} />
          <HelpSchuldSaldoVerzekeringBouwsparenBefore89 code={code} onClose={handleClearCode} />
          <HelpKapitaalaflossingLangetermijnsparen code={code} onClose={handleClearCode} />
        </>
      )}
    </div>
  );
};

TaxCodesForm.displayName = "TaxCodesForm";
